'use client';

import React, { createContext, useCallback, useContext, useSyncExternalStore } from 'react';

import { LIGHT_THEME_ENABLED, MESSAGES } from '@/constants';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// --- External Store Logic ---

const getSystemTheme = (): ThemeMode => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const getSavedTheme = (): ThemeMode | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('theme') as ThemeMode | null;
};

const store = {
  getSnapshot: (): ThemeMode => {
    if (!LIGHT_THEME_ENABLED) return 'dark';
    const saved = getSavedTheme();
    if (saved) return saved;
    return getSystemTheme();
  },
  getServerSnapshot: (): ThemeMode => (LIGHT_THEME_ENABLED ? 'light' : 'dark'),
  subscribe: (callback: () => void) => {
    // Listen for storage changes (other tabs)
    window.addEventListener('storage', callback);

    // Listen for system preference changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', callback);

    return () => {
      window.removeEventListener('storage', callback);
      mediaQuery.removeEventListener('change', callback);
    };
  },
  emitChange: () => {
    // Dispatch a dummy event to trigger all subscribers
    window.dispatchEvent(new Event('storage'));
  },
};

// --- Provider Component ---

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error(MESSAGES.CONTEXT.THEME_PROVIDER_REQUIRED);
  }
  return context;
};

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const mode = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getServerSnapshot);

  const setTheme = useCallback((newMode: ThemeMode) => {
    localStorage.setItem('theme', newMode);
    document.documentElement.setAttribute('data-theme', newMode);
    store.emitChange();
  }, []);

  const toggleTheme = useCallback(() => {
    const nextMode = mode === 'light' ? 'dark' : 'light';
    setTheme(nextMode);
  }, [mode, setTheme]);

  // Initial attribute sync (useLayoutEffect for immediate paint)
  React.useLayoutEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
